// Automation Control Panel
import React, { useState, useEffect } from 'react';
import { Play, Pause, BarChart3, Clock, Zap, AlertCircle, RefreshCw, Save } from 'lucide-react';
import { startSocialMediaAutomation, getSocialMediaAutomation } from '../modules/social-media/services/automationService';
import { startGoogleAdsAutomation, getGoogleAdsAutomation } from '../modules/google-ads/services/automationService';
import { automationStateService } from '../services/automationStateService';
import { useToast } from '../components/ui/Toast';

const AutomationPanel: React.FC = () => {
  const { showToast } = useToast();
  const [socialMediaRunning, setSocialMediaRunning] = useState(false);
  const [googleAdsRunning, setGoogleAdsRunning] = useState(false);
  const [weeklyReport, setWeeklyReport] = useState<string>('');
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
  const [stats, setStats] = useState(automationStateService.getStats());
  const [isSaving, setIsSaving] = useState(false);

  // Pobierz klucz API z localStorage lub z ustawień Universal AI
  const [apiKey, setApiKey] = useState<string>('');
  
  useEffect(() => {
    // Pobierz klucz API z localStorage lub z ustawień Universal AI
    const storedKey = localStorage.getItem('anthropic_api_key');
    if (storedKey) {
      setApiKey(storedKey);
    } else {
      // Spróbuj pobrać z ustawień modułu automation
      const API_URL = import.meta.env.PROD 
        ? 'https://api.plexisystem.pl/api'
        : 'http://localhost:3001/api';
      
      fetch(`${API_URL}/universal-ai/settings/automation`)
        .then(r => r.json())
        .then(data => {
          // Jeśli jest klucz i nie jest zamaskowany
          if (data.api_key && !data.api_key.startsWith('***')) {
            setApiKey(data.api_key);
            // Zapisz w localStorage dla szybszego dostępu
            localStorage.setItem('anthropic_api_key', data.api_key);
          } else if (data.has_key) {
            // Jeśli klucz istnieje ale jest zamaskowany, użyj tokena dla autoryzacji
            console.log('⚠️ Klucz API jest ustawiony w systemie ale zamaskowany');
          }
        })
        .catch(err => {
          console.error('Error fetching API key:', err);
          console.log('⚠️ Ustaw klucz API w Ustawieniach → Centrum AI');
        });
    }
  }, []);

  // Załaduj stan z serwisu przy montażu
  useEffect(() => {
    const socialState = automationStateService.getSocialMediaState();
    const googleState = automationStateService.getGoogleAdsState();
    setSocialMediaRunning(socialState.enabled);
    setGoogleAdsRunning(googleState.enabled);
    setStats(automationStateService.getStats());
  }, []);

  const toggleSocialMedia = async () => {
    console.log('toggleSocialMedia clicked, apiKey:', apiKey ? 'exists' : 'missing');
    
    if (!apiKey) {
      console.error('No API key found');
      showToast({ type: 'error', title: 'Brak klucza API', message: 'Ustaw klucz API Claude w ustawieniach.' });
      return;
    }
    
    setIsSaving(true);
    const newState = !socialMediaRunning;
    
    try {
      if (newState) {
        startSocialMediaAutomation(apiKey);
        showToast({ type: 'success', title: 'Automatyzacja włączona', message: 'Social Media AI rozpocznie pracę.' });
      } else {
        const automation = getSocialMediaAutomation();
        automation?.stopAutomation();
        showToast({ type: 'success', title: 'Automatyzacja wyłączona', message: 'Social Media AI zatrzymane.' });
      }
      
      setSocialMediaRunning(newState);
      automationStateService.toggleSocialMedia(newState);
      
      // Odśwież statystyki
      await automationStateService.fetchRealStats();
      setStats(automationStateService.getStats());
    } catch (error) {
      console.error('Error toggling social media:', error);
      showToast({ type: 'error', title: 'Błąd', message: 'Nie udało się przełączyć automatyzacji.' });
    }
    
    setTimeout(() => setIsSaving(false), 1000);
  };

  const toggleGoogleAds = async () => {
    console.log('toggleGoogleAds clicked, apiKey:', apiKey ? 'exists' : 'missing');
    
    if (!apiKey) {
      console.error('No API key found');
      showToast({ type: 'error', title: 'Brak klucza API', message: 'Ustaw klucz API Claude w ustawieniach.' });
      return;
    }
    
    setIsSaving(true);
    const newState = !googleAdsRunning;
    
    try {
      if (newState) {
        startGoogleAdsAutomation(apiKey);
        showToast({ type: 'success', title: 'Automatyzacja włączona', message: 'Google Ads AI rozpocznie optymalizację.' });
      } else {
        const automation = getGoogleAdsAutomation();
        // Google Ads nie ma stopAutomation, więc tylko zmieniamy stan
        showToast({ type: 'success', title: 'Automatyzacja wyłączona', message: 'Google Ads AI zatrzymane.' });
      }
      
      setGoogleAdsRunning(newState);
      automationStateService.toggleGoogleAds(newState);
      
      // Odśwież statystyki
      await automationStateService.fetchRealStats();
      setStats(automationStateService.getStats());
    } catch (error) {
      console.error('Error toggling google ads:', error);
      showToast({ type: 'error', title: 'Błąd', message: 'Nie udało się przełączyć automatyzacji.' });
    }
    
    setTimeout(() => setIsSaving(false), 1000);
  };

  const generateWeeklyReport = async () => {
    console.log('generateWeeklyReport clicked');
    try {
      const automation = getSocialMediaAutomation();
      if (automation) {
        const report = await automation.generateWeeklyReport();
        setWeeklyReport(report);
        showToast({ type: 'success', title: 'Raport gotowy', message: 'Raport tygodniowy został wygenerowany.' });
      } else {
        showToast({ type: 'warning', title: 'Uwaga', message: 'Najpierw włącz automatyzację Social Media.' });
      }
    } catch (error) {
      console.error('Error generating report:', error);
      showToast({ type: 'error', title: 'Błąd', message: 'Nie udało się wygenerować raportu.' });
    }
  };

  useEffect(() => {
    // Aktualizuj rzeczywiste statystyki co minutę
    const interval = setInterval(async () => {
      setLastUpdate(new Date());
      // Pobierz rzeczywiste dane z API
      await automationStateService.fetchRealStats();
      setStats(automationStateService.getStats());
    }, 60000);

    // Pobierz dane przy starcie
    automationStateService.fetchRealStats().then(() => {
      setStats(automationStateService.getStats());
    });

    return () => clearInterval(interval);
  }, []);

  const syncWithBackend = async () => {
    setIsSaving(true);
    await automationStateService.syncWithBackend();
    const socialState = automationStateService.getSocialMediaState();
    const googleState = automationStateService.getGoogleAdsState();
    setSocialMediaRunning(socialState.enabled);
    setGoogleAdsRunning(googleState.enabled);
    setStats(automationStateService.getStats());
    setTimeout(() => setIsSaving(false), 1000);
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="bg-zinc-800 rounded-lg shadow-xl p-6 border border-zinc-700">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold flex items-center text-white">
            <Zap className="w-8 h-8 text-yellow-500 mr-3" />
            Panel Automatyzacji AI
          </h1>
          <div className="flex gap-2">
            <button
              onClick={syncWithBackend}
              disabled={isSaving}
              className="px-3 py-2 bg-zinc-700 hover:bg-zinc-600 text-white rounded-lg transition-all flex items-center gap-2 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${isSaving ? 'animate-spin' : ''}`} />
              Synchronizuj
            </button>
            {isSaving && (
              <div className="px-3 py-2 bg-green-900/50 text-green-400 rounded-lg flex items-center gap-2">
                <Save className="w-4 h-4" />
                Zapisywanie...
              </div>
            )}
          </div>
        </div>

        <div className="bg-blue-900/20 border border-blue-800 rounded-lg p-4 mb-6">
          <div className="flex items-start">
            <AlertCircle className="w-5 h-5 text-blue-400 mt-0.5 mr-3" />
            <div>
              <p className="font-medium text-blue-300">Automatyzacja z minimalnym nakładem czasu</p>
              <p className="text-sm text-blue-400 mt-1">
                System będzie automatycznie generował i publikował treści, optymalizował kampanie
                i generował raporty. Wystarczy włączyć automatyzację poniżej.
              </p>
            </div>
          </div>
        </div>

        {/* Social Media Automation */}
        <div className="border border-zinc-700 rounded-lg p-6 mb-6 bg-zinc-900">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h2 className="text-lg font-semibold flex items-center text-white">
                Social Media AI
                {socialMediaRunning && (
                  <span className="ml-2 px-2 py-1 bg-green-900/50 text-green-400 text-xs rounded-full">
                    AKTYWNE
                  </span>
                )}
              </h2>
              <p className="text-sm text-gray-400 mt-1">
                Automatyczne generowanie i publikowanie postów
              </p>
            </div>
            <button
              onClick={toggleSocialMedia}
              className={`p-3 rounded-lg transition-all ${
                socialMediaRunning
                  ? 'bg-red-900/50 hover:bg-red-900/70 text-red-400'
                  : 'bg-green-900/50 hover:bg-green-900/70 text-green-400'
              }`}
            >
              {socialMediaRunning ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
            </button>
          </div>

          <div className="space-y-3 text-sm">
            <div className="flex items-center text-gray-400">
              <Clock className="w-4 h-4 mr-2" />
              Publikacje: 9:00, 13:00, 19:00
            </div>
            <div className="flex items-center text-gray-400">
              <BarChart3 className="w-4 h-4 mr-2" />
              Platformy: Facebook, LinkedIn, Instagram
            </div>
            
            {socialMediaRunning && (
              <div className="mt-4 p-3 bg-zinc-800 rounded border border-zinc-700">
                <p className="text-xs text-gray-500">
                  Ostatnia aktywność: {lastUpdate.toLocaleTimeString('pl-PL')}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Google Ads Automation */}
        <div className="border border-zinc-700 rounded-lg p-6 mb-6 bg-zinc-900">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h2 className="text-lg font-semibold flex items-center text-white">
                Google Ads AI
                {googleAdsRunning && (
                  <span className="ml-2 px-2 py-1 bg-green-900/50 text-green-400 text-xs rounded-full">
                    AKTYWNE
                  </span>
                )}
              </h2>
              <p className="text-sm text-gray-400 mt-1">
                Automatyczna optymalizacja kampanii i reklam
              </p>
            </div>
            <button
              onClick={toggleGoogleAds}
              className={`p-3 rounded-lg transition-all ${
                googleAdsRunning
                  ? 'bg-red-900/50 hover:bg-red-900/70 text-red-400'
                  : 'bg-green-900/50 hover:bg-green-900/70 text-green-400'
              }`}
            >
              {googleAdsRunning ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
            </button>
          </div>

          <div className="space-y-3 text-sm">
            <div className="flex items-center text-gray-400">
              <Clock className="w-4 h-4 mr-2" />
              Optymalizacja: Codziennie o 9:00
            </div>
            <div className="flex items-center text-gray-400">
              <BarChart3 className="w-4 h-4 mr-2" />
              Funkcje: Nowe reklamy, optymalizacja stawek, analiza konkurencji
            </div>
            
            {googleAdsRunning && (
              <div className="mt-4 p-3 bg-zinc-800 rounded border border-zinc-700">
                <p className="text-xs text-gray-500">
                  Następna optymalizacja: jutro o 9:00
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={generateWeeklyReport}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all"
          >
            Generuj raport tygodniowy
          </button>
          <button 
            onClick={() => window.location.href = '/api-settings'}
            className="px-4 py-2 bg-zinc-700 text-gray-300 rounded-lg hover:bg-zinc-600 transition-all"
          >
            Ustawienia automatyzacji
          </button>
        </div>

        {/* Weekly Report */}
        {weeklyReport && (
          <div className="mt-6 p-4 bg-zinc-900 rounded-lg border border-zinc-700">
            <pre className="whitespace-pre-wrap text-sm text-gray-300">{weeklyReport}</pre>
          </div>
        )}
      </div>

      {/* Real Stats from Service */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-zinc-800 rounded-lg shadow-xl p-4 border border-zinc-700">
          <h3 className="font-medium text-gray-400 mb-2">Dzisiaj</h3>
          <p className="text-2xl font-bold text-white">{stats.socialMedia.postsToday}</p>
          <p className="text-sm text-gray-500">posty opublikowane</p>
        </div>
        <div className="bg-zinc-800 rounded-lg shadow-xl p-4 border border-zinc-700">
          <h3 className="font-medium text-gray-400 mb-2">Ten tydzień</h3>
          <p className="text-2xl font-bold text-white">{stats.googleAds.campaignsOptimized}</p>
          <p className="text-sm text-gray-500">zoptymalizowanych kampanii</p>
        </div>
        <div className="bg-zinc-800 rounded-lg shadow-xl p-4 border border-zinc-700">
          <h3 className="font-medium text-gray-400 mb-2">Oszczędności</h3>
          <p className="text-2xl font-bold text-white">{Math.round(stats.timeSaved)}h</p>
          <p className="text-sm text-gray-500">zaoszczędzonego czasu</p>
        </div>
      </div>

      {/* Additional Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-zinc-800 rounded-lg shadow-xl p-4 border border-zinc-700">
          <h3 className="font-medium text-gray-400 mb-3">Social Media - Statystyki</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Posty w tym miesiącu:</span>
              <span className="text-white font-medium">{stats.socialMedia.postsMonth}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Posty w tym tygodniu:</span>
              <span className="text-white font-medium">{stats.socialMedia.postsWeek}</span>
            </div>
          </div>
        </div>
        <div className="bg-zinc-800 rounded-lg shadow-xl p-4 border border-zinc-700">
          <h3 className="font-medium text-gray-400 mb-3">Google Ads - Statystyki</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Utworzone reklamy:</span>
              <span className="text-white font-medium">{stats.googleAds.adsCreated}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Zaoszczędzony budżet:</span>
              <span className="text-white font-medium">{stats.googleAds.budgetSaved} PLN</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AutomationPanel;
